import { RawEntry, EntryType, Session, DayMap } from '../types';

export function parseTime(t: string): number | null {
  t = t.trim();
  
  // 12-hour format: e.g., "10:40 AM" or "2:30 PM"
  const match12 = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i);
  if (match12) {
    let hrs = parseInt(match12[1], 10);
    const mins = parseInt(match12[2], 10);
    const ampm = match12[3].toUpperCase();
    
    if (hrs < 1 || hrs > 12 || mins < 0 || mins > 59) {
      return null;
    }
    
    if (ampm === 'PM' && hrs !== 12) {
      hrs += 12;
    } else if (ampm === 'AM' && hrs === 12) {
      hrs = 0;
    }
    
    return Math.round((hrs + mins / 60) * 10000) / 10000;
  }
  
  // 24-hour format: e.g., "10:40" or "14:30"
  const match24 = t.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hrs = parseInt(match24[1], 10);
    const mins = parseInt(match24[2], 10);
    
    if (hrs < 0 || hrs > 23 || mins < 0 || mins > 59) {
      return null;
    }
    
    return Math.round((hrs + mins / 60) * 10000) / 10000;
  }
  
  return null;
}

export function parseLines(text: string, fallbackDate: string): RawEntry[] {
  const lines = text.split(/\r?\n/);

  // Detect and parse the multi-line "Time entries" block format
  const timeEntries = parseTimeEntriesBlock(lines, fallbackDate);
  if (timeEntries.length > 0) {
    return timeEntries;
  }

  // Detect and parse the multi-line "Work Date / Time In / Out" format
  const multiLineEntries = parseMultiLineTable(lines, fallbackDate);
  if (multiLineEntries.length > 0) {
    return multiLineEntries;
  }

  // Detect tab-separated table format (e.g. Toggl/Clockify exports)
  // Header like: "Date\tIn\tOut\tClient / Task\tTotal"
  const tableEntries = parseTabTable(lines, fallbackDate);
  if (tableEntries.length > 0) {
    return tableEntries;
  }

  // Existing pipe-delimited format
  const entries: RawEntry[] = [];
  let currentDate = fallbackDate;
  let lastInEntry: RawEntry | null = null;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    
    // Ignore markdown code block fences
    if (trimmed.startsWith('```')) {
      continue;
    }
    
    // Strip leading/trailing backticks and trim again
    const cleaned = trimmed.replace(/^`+|`+$/g, '').trim();
    if (!cleaned) {
      continue;
    }
    
    // Check if line matches YYYY-MM-DD and contains no pipe '|'
    const dateMatch = cleaned.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (dateMatch && !cleaned.includes('|')) {
      currentDate = dateMatch[1];
      lastInEntry = null;
      continue;
    }
    
    // Split on '|'
    if (cleaned.includes('|')) {
      const parts = cleaned.split('|').map(p => p.trim());
      if (parts.length >= 2) {
        const rawType = parts[0].toUpperCase();
        if (rawType === 'IN' || rawType === 'OUT') {
          // If there is a third part containing "Break" (case-insensitive), ignore the line
          if (parts.length >= 3 && /\bbreak\b/i.test(parts[2])) {
            continue;
          }
          const timeStr = parts[1];
          const timeDecimal = parseTime(timeStr);
          if (timeDecimal !== null) {
            let entryDate = currentDate;
            
            if (rawType === 'OUT' && lastInEntry && lastInEntry.date === currentDate) {
              if (timeDecimal < lastInEntry.time) {
                entryDate = addOneDay(currentDate);
              }
            }
            
            const newEntry: RawEntry = {
              date: entryDate,
              type: rawType as EntryType,
              time: timeDecimal,
              raw: cleaned
            };
            
            entries.push(newEntry);
            
            if (rawType === 'IN') {
              lastInEntry = newEntry;
            } else if (rawType === 'OUT') {
              lastInEntry = null;
            }
          }
        }
      }
    }
  }
  
  return entries;
}

const MONTH_MAP: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Parses a human-readable date like "Tue 16 Jun" or "16 Jun" into YYYY-MM-DD.
 * Infers the year from the fallback date context.
 */
function parseHumanDate(dateStr: string, fallbackDate: string): string | null {
  // Match patterns like "Tue 16 Jun", "16 Jun", "Mon 2 Jan"
  const match = dateStr.match(/(?:\w{3}\s+)?(\d{1,2})\s+(\w{3})/);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const monthKey = match[2].toLowerCase();
  const monthIndex = MONTH_MAP[monthKey];
  if (monthIndex === undefined || day < 1 || day > 31) return null;

  // Infer year from the fallback date
  const fallbackYear = parseInt(fallbackDate.substring(0, 4), 10) || new Date().getFullYear();

  const mm = String(monthIndex + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${fallbackYear}-${mm}-${dd}`;
}

/**
 * Parses tab-separated table rows with or without a header.
 * With header: looks for columns named "Date", "In"/"Start", "Out"/"End".
 * Without header: detects rows matching the pattern: Date\tHH:MM\tHH:MM\t...
 */
function parseTabTable(lines: string[], fallbackDate: string): RawEntry[] {
  // Try header-based detection first
  let startIdx = 0;
  let colDate = 0;
  let colIn = 1;
  let colOut = 2;
  let colRange = -1;
  let colClientTask = -1;
  let hasHeader = false;

  for (let i = 0; i < lines.length; i++) {
    const cols = lines[i].split('\t').map(c => c.trim().toLowerCase());
    if (cols.length < 3) continue;

    const dIdx = cols.findIndex(c => c === 'date' || c === 'work date');
    const iIdx = cols.findIndex(c => c === 'in' || c === 'start' || c === 'start time');
    const oIdx = cols.findIndex(c => c === 'out' || c === 'end' || c === 'end time');
    const rIdx = cols.findIndex(c => c === 'time in / out' || c === 'time range' || c === 'time');

    if (dIdx !== -1 && ((iIdx !== -1 && oIdx !== -1) || rIdx !== -1)) {
      startIdx = i + 1;
      colDate = dIdx;
      if (rIdx !== -1) {
        colRange = rIdx;
      } else {
        colIn = iIdx;
        colOut = oIdx;
      }
      hasHeader = true;

      const ctIdx = cols.findIndex(c => c === 'client / task' || c === 'client/task' || c === 'task' || c === 'client' || c === 'description');
      if (ctIdx !== -1) {
        colClientTask = ctIdx;
      }
      break;
    }
  }

  // If no header, check if lines look like tab-separated data rows
  if (!hasHeader) {
    const hasTabRows = lines.some(line => {
      const cols = line.split('\t');
      if (cols.length < 3) return false;
      return parseHumanDate(cols[0].trim(), fallbackDate) !== null
        && parseTime(cols[1].trim()) !== null
        && parseTime(cols[2].trim()) !== null;
    });
    if (!hasTabRows) return [];
  }

  const entries: RawEntry[] = [];

  for (let i = startIdx; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;

    const cols = trimmed.split('\t').map(c => c.trim());
    const minCols = colRange !== -1 ? Math.max(colDate, colRange) + 1 : Math.max(colDate, colIn, colOut) + 1;
    if (cols.length < minCols) continue;

    const dateStr = cols[colDate];
    let inTimeStr = '';
    let outTimeStr = '';

    if (colRange !== -1) {
      const rangeStr = cols[colRange];
      const rangeParts = rangeStr.split(/[\u2013-]/).map(p => p.trim());
      if (rangeParts.length === 2) {
        inTimeStr = rangeParts[0];
        outTimeStr = rangeParts[1];
      }
    } else {
      inTimeStr = cols[colIn];
      outTimeStr = cols[colOut];
    }

    // Parse date — try YYYY-MM-DD first, then human-readable / DD MMM YYYY
    const entryDate = dateStr.match(/^\d{4}-\d{2}-\d{2}$/) 
      ? dateStr 
      : (dateStr.match(/^\d{1,2}\s+[a-zA-Z]{3}\s+\d{4}$/) ? parseFormattedDate(dateStr) : parseHumanDate(dateStr, fallbackDate));
    if (!entryDate) continue;

    const inTime = parseTime(inTimeStr);
    const outTime = parseTime(outTimeStr);
    if (inTime === null || outTime === null) continue;

    // Determine Client/Task column
    let clientTaskVal: string | undefined = undefined;
    if (colClientTask !== -1 && cols.length > colClientTask) {
      clientTaskVal = cols[colClientTask];
    } else if (!hasHeader && cols.length > 3) {
      clientTaskVal = cols[3];
    }

    // Ignore lines with "Break" in Client/Task (case-insensitive)
    if (clientTaskVal && /\bbreak\b/i.test(clientTaskVal)) {
      continue;
    }

    // Determine OUT date (handle midnight rollover)
    let outDate = entryDate;
    if (outTime < inTime) {
      outDate = addOneDay(entryDate);
    }

    entries.push({
      date: entryDate,
      type: 'IN',
      time: inTime,
      raw: trimmed,
    });

    entries.push({
      date: outDate,
      type: 'OUT',
      time: outTime,
      raw: trimmed,
    });
  }

  return entries;
}

function addOneDay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() + 1);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseFormattedDate(dateStr: string): string | null {
  const match = dateStr.match(/^(\d{1,2})\s+([a-zA-Z]{3})\s+(\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const monthKey = match[2].toLowerCase();
  const year = parseInt(match[3], 10);
  const monthIndex = MONTH_MAP[monthKey];
  if (monthIndex === undefined || day < 1 || day > 31) return null;
  const mm = String(monthIndex + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function parseTimeRange(rangeStr: string): { inTime: number; outTime: number } | null {
  const parts = rangeStr.split(/[\u2013-]/).map(p => p.trim());
  if (parts.length !== 2) return null;
  const inTime = parseTime(parts[0]);
  const outTime = parseTime(parts[1]);
  if (inTime === null || outTime === null) return null;
  return { inTime, outTime };
}

function parseMultiLineTable(lines: string[], fallbackDate: string): RawEntry[] {
  const entries: RawEntry[] = [];
  
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }

    // Check if this line looks like a Date: "27 Jun 2026" or "27 Jun"
    const dateMatch = line.match(/^(\d{1,2})\s+([a-zA-Z]{3})(?:\s+(\d{4}))?$/);
    if (dateMatch) {
      // Look ahead for the time range line: "11:04 – 18:11"
      let nextLineIdx = i + 1;
      while (nextLineIdx < lines.length && !lines[nextLineIdx].trim()) {
        nextLineIdx++;
      }

      if (nextLineIdx < lines.length) {
        const timeLine = lines[nextLineIdx].trim();
        const timeRangeMatch = timeLine.match(/^\d{1,2}:\d{2}\s*[\u2013-]\s*\d{1,2}:\d{2}$/);
        if (timeRangeMatch) {
          const parsedDate = dateMatch[3] 
            ? parseFormattedDate(line) 
            : parseHumanDate(line, fallbackDate);
          const times = parseTimeRange(timeLine);
          
          if (parsedDate && times) {
            const { inTime, outTime } = times;
            let outDate = parsedDate;
            if (outTime < inTime) {
              outDate = addOneDay(parsedDate);
            }

            const rawString = `${line} | ${timeLine}`;

            entries.push({
              date: parsedDate,
              type: 'IN',
              time: inTime,
              raw: rawString,
            });

            entries.push({
              date: outDate,
              type: 'OUT',
              time: outTime,
              raw: rawString,
            });

            i = nextLineIdx + 1;
            continue;
          }
        }
      }
    }
    i++;
  }

  return entries;
}

function parseTimeEntriesBlock(lines: string[], fallbackDate: string): RawEntry[] {
  const entries: RawEntry[] = [];
  let currentDate = fallbackDate;
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }

    // 1. Detect if the line is a date header
    // YYYY-MM-DD
    if (line.match(/^\d{4}-\d{2}-\d{2}$/)) {
      currentDate = line;
      i++;
      continue;
    }
    // DD MMM YYYY or DD MMM
    const dateMatch = line.match(/^(\d{1,2})\s+([a-zA-Z]{3})(?:\s+(\d{4}))?$/);
    if (dateMatch) {
      const parsedDate = dateMatch[3] 
        ? parseFormattedDate(line) 
        : parseHumanDate(line, fallbackDate);
      if (parsedDate) {
        currentDate = parsedDate;
        i++;
        continue;
      }
    }
    // "Tue 16 Jun" or similar (starts with day name)
    if (line.match(/^[a-zA-Z]{3}\s+\d{1,2}\s+[a-zA-Z]{3}/) || line.match(/^[a-zA-Z]{3,9},\s+[a-zA-Z]{3}\s+\d{1,2}/)) {
      const parsedDate = parseHumanDate(line, fallbackDate);
      if (parsedDate) {
        currentDate = parsedDate;
        i++;
        continue;
      }
    }

    // 2. Detect time range pattern: "11:04 – 14:03"
    const timeRangeMatch = line.match(/^\d{1,2}:\d{2}\s*[\u2013-]\s*\d{1,2}:\d{2}$/);
    if (timeRangeMatch) {
      let descIdx = i + 1;
      while (descIdx < lines.length && !lines[descIdx].trim()) {
        descIdx++;
      }

      if (descIdx < lines.length) {
        const descLine = lines[descIdx].trim();
        
        let durIdx = descIdx + 1;
        while (durIdx < lines.length && !lines[durIdx].trim()) {
          durIdx++;
        }

        if (durIdx < lines.length) {
          const durLine = lines[durIdx].trim();
          const isDuration = /^(?:\d+h)?\s*(?:\d+m)?\s*(?:\d+s)?$/i.test(durLine) && durLine.length > 0;
          
          if (isDuration) {
            // Ignore if description is "Break" (case-insensitive)
            if (/\bbreak\b/i.test(descLine)) {
              i = durIdx + 1;
              continue;
            }

            const times = parseTimeRange(line);
            if (times) {
              const { inTime, outTime } = times;
              let outDate = currentDate;
              if (outTime < inTime) {
                outDate = addOneDay(currentDate);
              }

              const rawString = `${line} | ${descLine} | ${durLine}`;

              entries.push({
                date: currentDate,
                type: 'IN',
                time: inTime,
                raw: rawString,
              });

              entries.push({
                date: outDate,
                type: 'OUT',
                time: outTime,
                raw: rawString,
              });
            }

            i = durIdx + 1;
            continue;
          }
        }
      }
    }
    i++;
  }

  return entries;
}

export function computeSessions(entries: RawEntry[]): Session[] {
  const getTimestamp = (entry: RawEntry) => {
    // Date is stored as PHT (UTC+8). Parse the date components as local PHT
    // so adding the time-of-day in hours gives the correct absolute timestamp.
    // Without TZ-aware construction, `new Date('YYYY-MM-DDT00:00:00')` parses
    // as UTC midnight, which shifts every timestamp back 8 hours — orphaning
    // overnight OUTs and producing wildly long sessions that span midnight.
    const [y, m, d] = entry.date.split('-').map(Number);
    const phtMidnightUtc = Date.UTC(y, m - 1, d, 0, 0, 0) - 8 * 60 * 60 * 1000;
    return phtMidnightUtc + entry.time * 60 * 60 * 1000;
  };

  const sorted = [...entries].sort((a, b) => getTimestamp(a) - getTimestamp(b));
  const sessions: Session[] = [];
  const used = new Set<number>(); // indices of entries already consumed

  // Cap on a single session duration. Shifts are rarely >12h; 16h is the
  // practical ceiling (allows for the rare 14h overnight session). Anything
  // longer almost certainly means we'd cross-paired an IN with an OUT from
  // a different (adjacent) day.
  const MAX_SESSION_HOURS = 16;

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const current = sorted[i];
    if (current.type !== 'IN') continue;

    // Find the next OUT that:
    //  - hasn't been paired yet,
    //  - has a timestamp strictly after the IN's,
    //  - yields a duration <= MAX_SESSION_HOURS (rejects cross-day pairings).
    // Pick the OUT with the SMALLEST positive duration (closest match).
    let pairedIdx = -1;
    let pairedDuration = Infinity;
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;
      if (sorted[j].type !== 'OUT') continue;
      const dur = (getTimestamp(sorted[j]) - getTimestamp(current)) / (60 * 60 * 1000);
      if (dur <= 0) continue;
      if (dur > MAX_SESSION_HOURS) continue;
      if (dur < pairedDuration) {
        pairedDuration = dur;
        pairedIdx = j;
      }
    }

    if (pairedIdx === -1) continue;

    const next = sorted[pairedIdx];
    // Skip zero-duration sessions (IN and OUT at the same minute). These
    // show up as "12:02-12:02 / 0h 0m" rows in the day log and are almost
    // always accidental scans (double-tap or scan-out + scan-in back-to-back).
    // Drop them so the day list shows only real entries.
    if (pairedDuration === 0) continue;
    sessions.push({
      date: current.date, // Attribute the session to the start date
      in: current.time,
      out: next.time,
      duration: pairedDuration
    });
    used.add(i);
    used.add(pairedIdx);
  }

  return sessions;
}

export function groupByDate(sessions: Session[]): DayMap {
  const map: DayMap = {};
  for (const session of sessions) {
    if (!map[session.date]) {
      map[session.date] = 0;
    }
    map[session.date] += session.duration;
  }
  return map;
}
